import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { preFixImgUrl } from '@/utils'
import { OrderStatusEnum, PlatformEnum } from '@/service/api/types'
import type { OrderDetailResponse } from '@/service/api/orderPage';
import utils from '@/utils'
import classNames from 'classnames'
import OutLink from '@/components/OutLink'
import { JAPAN_MALL_ORDER_URL, VIP_ORDER_URL, SAGAWA_ORDER_URL } from '@/constants'


const getOrderUrl = (platform: PlatformEnum, orderId: string) => {
  switch (platform) {
    case PlatformEnum.JAPANMALL:
      return JAPAN_MALL_ORDER_URL.replace('{{id}}', orderId);
    case PlatformEnum.VIP:
      return VIP_ORDER_URL.replace('{{id}}', orderId);
    case PlatformEnum.SGW:
      return SAGAWA_ORDER_URL.replace('{{id}}', orderId);
  }
}

interface OrderItemProps {
  order: OrderDetailResponse;
}

const OrderItem = (props: OrderItemProps) => {
  const { order } = props;
  const { t } = useTranslation();

  const productSummary = useMemo(() => {
    return order.productSummary?.split(',') || [];
  }, [order.productSummary]);

  const orderStatus = useMemo(() => {
    switch (order.status) {
      case OrderStatusEnum.PROCESSING:
        return <LangText lngKey='e69ea44d' className='text-[12px] text-[#FF4D4F] border border-[#FF4D4F] rounded-[11px] h-[20px] px-2 leading-[18px]'>{t('e40e29b3')}</LangText>
      case OrderStatusEnum.COMPLETED:
        return <LangText lngKey='9999b785' className='text-[12px] text-[#4CAF50] border border-[#4CAF50] rounded-[11px] h-[20px] px-2 leading-[18px]'>{t('9999b785')}</LangText>
      case OrderStatusEnum.CANCELLED:
        return <LangText lngKey='4aaa5de9' className='text-[12px] text-[#666666] border border-[#666666] rounded-[11px] h-[20px] px-2 leading-[18px]'>{t('4aaa5de9')}</LangText>
      default:
        return '--'
    }
  }, [order.status])

  const orderTitleKey = useMemo(() => {
    switch (order.platform) {
      case PlatformEnum.JAPANMALL:
        return '738431c7'
      case PlatformEnum.SGW:
        return order.title || '';
      case PlatformEnum.VIP:
        return 'd4ff39b6';
      default:
        return '--'
    }
  }, [order.platform])

  const orderImg = useMemo(()=> {
    switch (order.platform) {
      case PlatformEnum.JAPANMALL:
        return preFixImgUrl('10d02dcb50c7d1eb201dff09a36f0bd6.png')
      case PlatformEnum.SGW:
        return preFixImgUrl('e7075ea55b19512edc13dc2162ec3cbc.png')
      case PlatformEnum.VIP:
        return preFixImgUrl('7f50bcc9af1c4e2192626a029afc0881.png')
    }
  },[order.platform])

  return (
    <div className='bg-[#F5F5F5] rounded-[8px] px-5'>
      <div className='h-[42px] flex items-center border-b border-[#D9D9D9]'>
        <LangText lngKey='7fbfccb4' className='text-[12px] text-[#666666]'>{t('7fbfccb4')}{t('eb540e57')}</LangText>
        <span className='text-[12px] text-[#666666]'>{order.orderNo}</span>
        <span className='w-[1px] h-3 bg-[#D9D9D9] mx-4'></span>
        <span className='text-[12px] text-[#666666]'>{order.createTime}</span>
      </div>
      <div className='flex justify-between items-center pt-2 pb-4'>
        <div className='flex items-center'>
          <img src={orderImg} className='w-11 h-11 mr-3 rounded-[8px]' />
          <div className='flex flex-col'>
            <div className='flex gap-2'>
              <LangText lngKey={orderTitleKey} className='text-[14px]'>{t(orderTitleKey)}</LangText>
              {orderStatus}
            </div>
            <div className='flex gap-2 mt-[10px] flex-wrap h-5'>
              {
                productSummary.map((item) => {
                  return <LangText key={item} className='bg-[#E0EFFF] text-[#5a52ff] text-[12px] leading-[22px] rounded-[11px] px-2' lngKey={item}>{item}</LangText>
                })
              }
            </div>
          </div>
        </div>
        <div className='flex flex-col items-end'>
          <LangText className='text-[#666666] text-[15px]' lngKey='218033e3'>{t('218033e3')}</LangText>
          <span className={classNames('text-[20px] font-bold', { 'text-[#FF4D4F]': order.status === OrderStatusEnum.PROCESSING })}>JPY {utils.addCommasToPrice(order.amount || 0)}</span>
          <div className='flex gap-4 leading-[32px] text-[14px] mt-2'>
            <OutLink platform={order.platform} linUrl={getOrderUrl(order.platform, order.orderNo)}>
              <div className='px-4 bg-white rounded-[16px] outline outline-[#D9D9D9] font-normal cursor-pointer'>
                <LangText className='' lngKey='961d88e2'>{t('961d88e2')}</LangText>
              </div>
            </OutLink>
            {!order.isPay && order.status === OrderStatusEnum.PROCESSING &&
              <OutLink platform={order.platform} linUrl={getOrderUrl(order.platform, order.orderNo)}>
                <div className='px-4 bg-white rounded-[16px] bg-linear-to-r from-[#a75aff] to-[#4b51ff] text-white cursor-pointer'>
                  <LangText className='' lngKey='f1352c02'>{t('f1352c02')}</LangText>
                </div>
              </OutLink>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderItem